import type { SceneObject } from '../../../types';
import type { DrawingState } from './types';
import { isBoxDrawingTool } from './types';

export type DrawingStyleOptions = {
  snapValue: (value: number) => number;
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  strokeStyle: NonNullable<SceneObject['strokeStyle']>;
  zIndex: number;
};

const MIN_BOX_SIZE = 10;

/**
 * Tamamlanmış çizim state'inden sahne objesi üretir.
 * Kutu şekilleri minimum boyutun altındaysa null döner.
 */
export function buildShapeFromDrawing(
  state: DrawingState,
  options: DrawingStyleOptions
): SceneObject | null {
  if (!state.isDrawing || !state.tool) return null;

  const { snapValue, fillColor, strokeColor, strokeWidth, strokeStyle, zIndex } = options;
  const x0 = state.startX;
  const y0 = state.startY;
  const x2 = state.currentX;
  const y2 = state.currentY;

  if (state.tool === 'curve') {
    const cx = state.controlX ?? (x0 + x2) / 2;
    const cy = state.controlY ?? (y0 + y2) / 2;
    const minX = Math.min(x0, cx, x2);
    const minY = Math.min(y0, cy, y2);
    const maxX = Math.max(x0, cx, x2);
    const maxY = Math.max(y0, cy, y2);
    const w = Math.max(MIN_BOX_SIZE, maxX - minX);
    const h = Math.max(MIN_BOX_SIZE, maxY - minY);
    const pathData = `M ${x0 - minX} ${y0 - minY} Q ${cx - minX} ${cy - minY} ${x2 - minX} ${y2 - minY}`;

    return {
      id: `obj-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: 'Curve',
      type: 'shape',
      shapeType: 'path',
      pathData,
      x: snapValue(minX),
      y: snapValue(minY),
      width: w,
      height: h,
      rotation: 0,
      opacity: 1,
      visible: true,
      locked: false,
      pivotPoint: 'center',
      zIndex,
      fill: fillColor,
      stroke: strokeColor,
      strokeWidth,
      strokeStyle,
    };
  }

  if (isBoxDrawingTool(state.tool)) {
    const width = Math.abs(x2 - x0);
    const height = Math.abs(y2 - y0);
    if (width < MIN_BOX_SIZE || height < MIN_BOX_SIZE) return null;

    const x = Math.min(x0, x2);
    const y = Math.min(y0, y2);
    const shapeInfo = {
      rectangle: { name: 'Rectangle', shapeType: 'rectangle' as const },
      circle: { name: 'Circle', shapeType: 'circle' as const },
      triangle: { name: 'Triangle', shapeType: 'triangle' as const },
    }[state.tool];

    return {
      id: `obj-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: shapeInfo.name,
      type: 'shape',
      shapeType: shapeInfo.shapeType,
      x: snapValue(x),
      y: snapValue(y),
      width,
      height,
      rotation: 0,
      opacity: 1,
      visible: true,
      locked: false,
      pivotPoint: 'center',
      zIndex,
      fill: fillColor,
      stroke: strokeColor,
      strokeWidth,
      strokeStyle,
    };
  }

  return null;
}
